import { Injectable, inject } from '@angular/core';
import type { PdfExportJob } from '@framework/analytics/models/report-pdf-export.model';
import {
	PDF_EXPORT_GENERIC_ERROR,
	PDF_EXPORT_TIMEOUT_ERROR,
} from '@framework/analytics/models/report-pdf-export.model';
import {
	parsePdfExportJob,
	pdfExportFailureMessage,
	triggerPdfDownload,
} from '@framework/analytics/utils/report-pdf-export.util';
import { RequestService } from './request.service';
import { reportAnalyticsError } from '../utils/analytics-error-reporting.util';

const REQUEST_MUTATION = `
  mutation RequestReportPdfExport($viewId: ID!, $accountId: ID) {
    requestReportPdfExport(viewId: $viewId, accountId: $accountId) {
      jobId
      status
      viewId
      accountId
      downloadUrl
      errorMessage
    }
  }
`;

const POLL_QUERY = `
  query GetReportPdfExportJob($jobId: ID!, $accountId: ID) {
    getReportPdfExportJob(jobId: $jobId, accountId: $accountId) {
      jobId
      status
      viewId
      accountId
      downloadUrl
      errorMessage
    }
  }
`;

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_TIMEOUT_MS = 180_000;

@Injectable({
	providedIn: 'root',
})
export class ReportPdfExportService {
	private readonly requestService = inject(RequestService);

	/**
	 * Request a PDF export for a shared report (IAM guest path).
	 * Polls until READY/FAILED, then downloads via triggerPdfDownload.
	 * Throws when READY has no downloadUrl or AppSync rejects the mutation.
	 */
	async downloadReportPdf(
		accountId: string,
		viewId: string,
		options?: {
			pollIntervalMs?: number;
			pollTimeoutMs?: number;
			openDownload?: boolean;
		}
	): Promise<PdfExportJob> {
		const pollIntervalMs =
			options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
		const pollTimeoutMs = options?.pollTimeoutMs ?? DEFAULT_POLL_TIMEOUT_MS;
		const openDownload = options?.openDownload !== false;

		let requestResponse;
		try {
			requestResponse = await this.requestService.execute(
				REQUEST_MUTATION,
				{ viewId, accountId }
			);
		} catch (error) {
			if (
				error instanceof Error &&
				error.message === 'AppSyncUnauthorized'
			) {
				throw new Error(
					'PDF download is not enabled for shared report links on this environment yet. Deploy AppSync IAM auth for requestReportPdfExport, or export from the Jepto app.'
				);
			}
			throw error;
		}
		this.throwIfGraphQlErrors(requestResponse, 'requestReportPdfExport');
		const job = this.parseJob(
			requestResponse?.data?.requestReportPdfExport,
			'requestReportPdfExport'
		);

		const finalJob =
			job.status === 'READY' || job.status === 'FAILED'
				? job
				: await this.pollUntilSettled(
						job.jobId,
						accountId,
						pollIntervalMs,
						pollTimeoutMs
					);

		if (finalJob.status === 'FAILED') {
			throw new Error(pdfExportFailureMessage(finalJob));
		}

		if (!finalJob.downloadUrl?.trim()) {
			throw new Error(PDF_EXPORT_GENERIC_ERROR);
		}

		if (openDownload) {
			await triggerPdfDownload(
				finalJob.downloadUrl,
				`report-${viewId}.pdf`
			);
		}

		return finalJob;
	}

	private async pollUntilSettled(
		jobId: string,
		accountId: string,
		pollIntervalMs: number,
		pollTimeoutMs: number
	): Promise<PdfExportJob> {
		const deadline = Date.now() + pollTimeoutMs;
		while (Date.now() < deadline) {
			await this.sleep(pollIntervalMs);
			const pollResponse = await this.requestService.execute(POLL_QUERY, {
				jobId,
				accountId,
			});
			this.throwIfGraphQlErrors(pollResponse, 'getReportPdfExportJob');
			const job = this.parseJob(
				pollResponse?.data?.getReportPdfExportJob,
				'getReportPdfExportJob'
			);
			if (job.status === 'READY' || job.status === 'FAILED') {
				return job;
			}
		}
		throw new Error(PDF_EXPORT_TIMEOUT_ERROR);
	}

	private throwIfGraphQlErrors(
		gqlResponse: unknown,
		operation: string
	): void {
		const errors = (gqlResponse as { errors?: Array<{ message?: string }> })
			?.errors;
		if (!Array.isArray(errors) || !errors.length) {
			return;
		}

		const message = errors[0]?.message ?? PDF_EXPORT_GENERIC_ERROR;
		throw new Error(message);
	}

	private parseJob(raw: unknown, operation: string): PdfExportJob {
		try {
			return parsePdfExportJob(raw);
		} catch (error) {
			reportAnalyticsError(
				`${operation} returned an invalid job`,
				error,
				{
					feature: 'analytics',
					operation,
				}
			);
			throw error;
		}
	}

	private sleep(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}
